// MST
import { types } from 'mobx-state-tree'

// Lodash
import omit from 'lodash/omit'

// Js File Download
import fileDownload from 'js-file-download'

// Content Disposition
import contentDisposition from 'content-disposition'

// Constant
import { GENDERS } from 'constant/shared.constant'

// Types
const { maybeNull, optional, array, model, maybe, enumeration, number, string, boolean } = types

// Assessor Props
const assessorProps = {
  number: maybeNull(number),
  id: maybeNull(number),
  userName: maybeNull(string),
  userId: maybeNull(number),
  bidangSertifikasiName: maybeNull(string),
  bidangSertifikasiId: maybeNull(number),
  skemaSertifikasiName: maybeNull(string),
  skemaSertifikasiId: maybeNull(number),
  code: maybeNull(string),
  expiredAt: maybeNull(string),
  createdAt: maybeNull(string),
  startExpiredAt: maybeNull(string),
  endExpiredAt: maybeNull(string),
  updatedAt: maybeNull(string),
  validityPeriod: maybeNull(string),
}

// TUK Props
const tukProps = {
  number: maybeNull(number),
  id: maybeNull(number),
  imageUrl: maybeNull(string),
  originalFileName: maybeNull(string),
  companyName: maybeNull(string),
  phoneNumber: maybeNull(string),
  address: maybeNull(string),
  status: maybeNull(boolean),
}

/**
 * @description Mobx types for base api response with pagination
 *
 * @param {any} list
 * @param {object} options
 *
 * @return {object} object
 */
export const withHelper_baseResponsePaginationTypes = (list: any, options?: any) => {
  return {
    currentPage: optional(number, 0),
    pageNumber: optional(number, 0),
    itemsPerPage: optional(number, 0),
    sortColumn: optional(string, ''),
    sortDirection: optional(boolean, false),
    totalData: optional(number, 0),
    totalPage: optional(number, 0),
    pageSize: optional(number, 0),
    list: options?.notArray ? optional(list, {}) : optional(array(list), []),
  }
}

/**
 * @description Mobx types for base api response of dropdown
 *
 * @param {string} modelName
 *
 * @return {any} any
 */
export const withHelper_baseResponseDropdownTypesModel = (modelName: string) => {
  return optional(
    array(
      model(modelName).props({
        label: optional(string, ''),
        value: maybeNull(string),
      }),
    ),
    [],
  )
}

/**
 * @description Mobx types for base api response list of data in webpage
 *
 * @return {any} any
 */
export const withHelper_baseResponseWebpage = () => {
  const ContentListObjectModel = model('ContentListObject').props({
    id: optional(number, 0),
    title: maybeNull(string),
    imageURL: maybeNull(string),
  })

  const ItemListModel = model('ItemList').props({
    id: optional(number, 0),
    title: maybeNull(string),
    description: maybeNull(string),
    imageURL: maybeNull(string),
    content: optional(array(ContentListObjectModel), []),
  })

  const SectionListModel = model('SectionList').props({
    id: optional(number, 0),
    title: maybeNull(string),
    description: maybeNull(string),
    imageURL: maybeNull(string),
    items: optional(array(ItemListModel), []),
  })

  const WebpageModel = model('Webpage').props({
    id: optional(number, 0),
    title: maybeNull(string),
    description: maybeNull(string),
    imageURL: maybeNull(string),
    sections: optional(array(SectionListModel), []),
  })

  return optional(WebpageModel, {})
}

/**
 * @description Base news
 *
 * @return {any} any
 */
export const withHelper_baseNews = () => {
  return model('News').props({
    id: maybeNull(number),
    title: maybeNull(string),
    description: maybeNull(string),
    imageURL: maybeNull(string),
  })
}

/**
 * @description Base news Detail
 *
 * @return {any} any
 */
export const withHelper_baseNewsDetail = () => {
  return model('NewsDetail').props({
    id: maybeNull(number),
    title: maybeNull(string),
    description: maybeNull(string),
    imageURL: maybeNull(string),
    publishedDate: maybeNull(string),
    author: maybeNull(string),
    createdAt: maybeNull(string),
    updatedAt: maybeNull(string),
  })
}

/**
 * @description Assessor List
 *
 * @return {any} any
 */
export const withHelper_assessors = () => {
  return model('AssessorList').props(omit(assessorProps, ['bidangSertifikasiId', 'skemaSertifikasiId']))
}

/**
 * @description Assessor Detail
 *
 * @return {any} any
 */
export const withHelper_assessor = () => {
  return model('AssessorDetail').props(omit(assessorProps, ['number']))
}

/**
 * @description TUK List
 *
 * @return {any} any
 */
export const withHelper_tukList = () => {
  return model('TukList').props(omit(tukProps, ['originalFileName']))
}

/**
 * @description TUK Detail
 *
 * @return {any} any
 */
export const withHelper_tuk = () => {
  return model('TukDetail').props(tukProps)
}

/**
 * @description Download file from server
 *
 * @param {BlobPart} blobPart
 * @param {object} headers
 *
 * @return {void} void
 */
export const withHelper_fileDownload = (blobPart: BlobPart, headers: any): void => {
  const _headers = headers as { 'content-disposition': string }
  const _contentDisposition = _headers?.['content-disposition'] || ''

  if (_contentDisposition) {
    fileDownload(blobPart, contentDisposition.parse(_contentDisposition).parameters.filename)
  }
}

/**
 * @description Certification Document Proof
 *
 * @return {any} any
 */
export const withHelper_certificationDocumentProof = () => {
  return model('CertificationDocumentProof').props({
    id: maybeNull(number),
    documentProofName: maybeNull(string),
    proof: maybeNull(string),
    userOriginalFileName: maybeNull(string),
    userFileBlobId: maybeNull(string),
    isActionLoading: maybe(optional(boolean, false)),
  })
}

/**
 * @description Document Support
 *
 * @return {any} any
 */
export const withHelper_documentSupportProps = {
  id: maybeNull(number),
  categoryName: maybeNull(string),
  programTypeName: maybeNull(string),
  type: maybeNull(string),
  documentProofName: maybeNull(string),
  point: maybeNull(number),
  status: maybeNull(string),
  originalFileName: maybeNull(string),
  fileBlobId: maybeNull(string),
  userOriginalFileName: maybeNull(string),
  userFileBlobId: maybeNull(string),
  notes: maybeNull(string),
  isMandatory: maybeNull(boolean),
  fileMimes: maybeNull(optional(array(string), [])),

  // For Front-End only
  isActionLoading: maybe(optional(boolean, false)),
}

// Unit Competency Props
export const withHelper_unitCompetencyProps = {
  competencyUnitId: maybeNull(number),
  competencyUnitCode: maybeNull(string),
  competencyUnitTitle: maybeNull(string),
  competencyUnitName: maybeNull(string),
  competentSummary: maybeNull(number),
  incompetentSummary: maybeNull(number),
  finishedCompetency: maybeNull(number),
  totalCompetency: maybeNull(number),
}

// Element Competency Props
export const withHelper_elementCompetencyProps = {
  competencyElementId: maybeNull(number),
  competencyElementName: maybeNull(string),
  competencyUnitId: maybeNull(number),
}

// Competency Props
export const withHelper_competencyProps = {
  competencyId: maybeNull(number),
  kukNumber: maybeNull(number),
  question: maybeNull(string),
  assessment: maybeNull(string),
  proof: maybeNull(string),
  assessorAssessment: optional(array(string), []),
  userOriginalFileName: maybeNull(string),
  userFileBlobId: maybeNull(string),
  competencyUnitId: maybeNull(number),
  competencyElementId: maybeNull(number),

  // !For Front-End Only
  isActionLoading: maybe(optional(boolean, false)),
}

// My Document Props
export const withHelper_myDocumentProps = {
  id: maybeNull(number),
  programTypeName: maybeNull(string),
  point: maybeNull(number),
  categoryName: maybeNull(string),
  type: maybeNull(string),
  userOriginalFileName: maybeNull(string),
  userFileBlobId: maybeNull(string),
}

export const withHelper_myDocumentDetailProps = {
  ...withHelper_myDocumentProps,
  programTypeId: maybeNull(string),
  categoryId: maybeNull(string),
  typeId: maybeNull(string),
}

// Pagination Props
export const withHelper_paginationProps = {
  currentPage: maybe(maybeNull(number)),
  pageSize: maybe(maybeNull(number)),
  totalData: maybe(maybeNull(number)),
  totalPage: maybe(maybeNull(number)),
}

// Profile Data Props
export const withHelper_profileDataProps = {
  profilePhoto: maybeNull(string),
  name: maybeNull(string),
  email: maybeNull(string),
  phoneNumber: maybeNull(string),
  address: maybeNull(string),
  birthDate: maybeNull(string),
  npk: maybeNull(string),
  nik: maybeNull(string),
  isPhoneNumberValidated: maybeNull(boolean),
  birthPlace: maybeNull(string),
  nationality: maybeNull(string),
  postalCode: maybeNull(string),
  phone: maybeNull(string),
  lastEducation: maybeNull(string),
  gender: maybeNull(enumeration('Gender', Object.values(GENDERS))),
  workPhone: maybeNull(string),
  institutionName: maybeNull(string),
  idCardPhoto: maybeNull(string),
  pasPhoto: maybeNull(string)
}

// Company Data Props
export const withHelper_companyDataProps = {
  companyId: maybeNull(number),
  companyName: maybeNull(string),
  branchId: maybeNull(number),
  branchName: maybeNull(string),
  divisionId: maybeNull(number),
  divisionName: maybeNull(string),
  companyAddress: maybeNull(string),
  supervisorName: maybeNull(string),
  supervisorEmail: maybeNull(string),
  idCardPhoto: maybeNull(string),
  bachelorCertificatePhoto: maybeNull(string),
  familyCardPhoto: maybeNull(string),
  workCardPhoto: maybeNull(string),
  jobTitle: maybeNull(string),
  companyPostalCode: maybeNull(string),
  companyEmail: maybeNull(string),
  companyFax: maybeNull(string),
  companyPhone: maybeNull(string),
  fieldId: maybeNull(number),
  fieldName: maybeNull(string),
}
